import {
  ChartLegend,
  ChartMarker,
  chartSeriesStyle,
  DataTable,
  useTranslate,
  FOCUS_RING,
} from '@athena/ui'
import { formatDay, type Language } from '@athena/i18n'
import { aiFormatting, providerName } from '../../lib/ai-analytics.js'
import type { AiProviderComparisonQuery } from '../../graphql/generated/operations.js'

type Daily = AiProviderComparisonQuery['adminAIProviderAnalytics']['daily'][number]
type Metric = 'requests' | 'latency' | 'cost' | 'success' | 'tokens' | 'editing'
export function AITrend({
  title,
  metric,
  daily,
  language,
}: {
  title: string
  metric: Metric
  daily: Daily[]
  language: Language
}) {
  const t = useTranslate()
  const format = aiFormatting(language, t)
  const observedDays = [...new Set(daily.map((d) => d.day))].sort()
  const days: string[] = []
  if (observedDays.length) {
    for (
      let time = Date.parse(observedDays[0]!);
      time <= Date.parse(observedDays.at(-1)!);
      time += 86400000
    )
      days.push(new Date(time).toISOString())
  }
  const series = ['openai', 'gemini'].flatMap((provider, index) =>
    metric === 'latency'
      ? [
          { provider, index, p95: false },
          { provider, index: index + 2, p95: true },
        ]
      : [{ provider, index, p95: false }],
  )
  function value(d: Daily, p95 = false): number | null {
    switch (metric) {
      case 'requests':
        return d.requests
      case 'latency':
        return (p95 ? d.p95LatencyMs : d.averageLatencyMs) ?? null
      case 'cost':
        return d.totalCostUsd ?? null
      case 'success':
        return d.requests ? (d.succeeded / d.requests) * 100 : null
      case 'tokens':
        return d.tokenSamples && d.totalTokens != null ? d.totalTokens / d.tokenSamples : null
      case 'editing':
        return d.editingSamples ? (d.editedPractices / d.editingSamples) * 100 : null
    }
  }
  const peak = Math.max(
    0,
    ...daily.flatMap((d) => [value(d) ?? 0, metric === 'latency' ? (value(d, true) ?? 0) : 0]),
  )
  const max = peak || 1
  const width = Math.max(600, days.length * 32 + 90)
  const y = (v: number) => 160 - (v / max) * 130
  const x = (i: number) => 75 + (i * (width - 100)) / Math.max(1, days.length - 1)
  const show = (v: number | null | undefined) =>
    metric === 'cost'
      ? format.money(v)
      : metric === 'latency'
        ? format.latency(v)
        : (metric === 'success' || metric === 'editing') && v != null
          ? format.rate(v, 100)
          : format.number(v)
  return (
    <figure className="flex min-w-0 flex-col gap-3 border-t border-border py-4">
      <figcaption className="text-h4 font-semibold">{title}</figcaption>
      <ChartLegend
        label={t('aiAnalytics.chartLegend')}
        items={series.map((s) => ({
          id: `${s.provider}-${s.p95}`,
          name: `${providerName(s.provider)}${metric === 'latency' ? ` · ${s.p95 ? t('aiAnalytics.p95Latency') : t('aiAnalytics.averageLatency')}` : ''}`,
          styleIndex: s.index,
        }))}
      />
      <div className="overflow-x-auto">
        <svg
          width={width}
          height={210}
          aria-hidden="true"
          className="text-caption text-text-secondary"
        >
          {[0, 0.5, 1].map((tick) => (
            <g key={tick}>
              <line
                x1={70}
                x2={width - 15}
                y1={y(max * tick)}
                y2={y(max * tick)}
                stroke="var(--color-border)"
              />
              <text x={65} y={y(max * tick) + 4} textAnchor="end" fill="currentColor">
                {show(max * tick)}
              </text>
            </g>
          ))}
          {days.map((d, i) =>
            i % Math.max(1, Math.ceil(days.length / 6)) === 0 || i === days.length - 1 ? (
              <text key={d} x={x(i)} y={193} textAnchor="middle" fill="currentColor">
                {formatDay(d, language, { utc: true })}
              </text>
            ) : null,
          )}
          {series.map((s) => {
            const style = chartSeriesStyle(s.index)
            let connected = false
            const points: { x: number; y: number }[] = []
            const path = days
              .map((day, i) => {
                const d = daily.find((d) => d.day === day && d.provider === s.provider)
                const v = d ? value(d, s.p95) : metric === 'requests' ? 0 : null
                if (v === null) {
                  connected = false
                  return ''
                }
                const point = { x: x(i), y: y(v) }
                points.push(point)
                const segment = `${connected ? 'L' : 'M'} ${point.x} ${point.y}`
                connected = true
                return segment
              })
              .join(' ')
            return (
              <g key={`${s.provider}-${s.p95}`}>
                <path
                  d={path}
                  fill="none"
                  stroke={style.color}
                  strokeWidth={2}
                  strokeDasharray={style.dash}
                />
                {points.map((p) => (
                  <ChartMarker key={p.x} cx={p.x} cy={p.y} shape={style.shape} fill={style.color} />
                ))}
              </g>
            )
          })}
        </svg>
      </div>
      <details>
        <summary className={`cursor-pointer rounded-sm text-body-sm text-primary ${FOCUS_RING}`}>
          {t('aiAnalytics.chartTable')}
        </summary>
        <DataTable
          caption={title}
          rows={daily}
          rowKey={(r) => `${r.day}-${r.provider}`}
          columns={[
            {
              id: 'day',
              header: t('aiAnalytics.timestamp'),
              cell: (r) => formatDay(r.day, language, { utc: true }),
            },
            {
              id: 'provider',
              header: t('aiAnalytics.provider'),
              cell: (r) => providerName(r.provider),
            },
            { id: 'value', header: title, cell: (r) => show(value(r)) },
            ...(metric === 'latency'
              ? [
                  {
                    id: 'p95',
                    header: t('aiAnalytics.p95Latency'),
                    cell: (r: Daily) => show(value(r, true)),
                  },
                ]
              : []),
            {
              id: 'samples',
              header: t('aiAnalytics.requests'),
              cell: (r) =>
                t('aiAnalytics.samples', [
                  metric === 'latency'
                    ? r.latencySamples
                    : metric === 'cost'
                      ? r.costSamples
                      : metric === 'tokens'
                        ? r.tokenSamples
                        : metric === 'editing'
                          ? r.editingSamples
                          : r.requests,
                  r.requests,
                ]),
            },
          ]}
        />
      </details>
    </figure>
  )
}
